import React, { useCallback, useEffect, useState, useRef } from "react";
import { useAppDispatch, useAppSelector } from "@app/hooks";
import RequestContactUpdateTextarea from "@requestContactUpdate/components/RequestContactUpdateTextarea";
import RequestContactUpdateEntitiesDropdowns from "@requestContactUpdate/components/RequestContactUpdateEntitiesDropdowns";
import RequestContactUpdateInput from "@requestContactUpdate/components/RequestContactUpdateInput";
import { updateCommonRequestData } from "@requestContactUpdate/store/ducks";
import {
  isValidName,
  isValidEmail,
  MAX_CONTACT_NAME_LENGTH,
  MAX_CONTACT_EMAIL_LENGTH,
} from "@requestContactUpdate/utils/validation";
import {
  requestContactUpdatePreparedDataSelector,
  requestContactUpdateSelector,
} from "@requestContactUpdate/store/selectors";
import RequestContactUpdateFormTitle from "@requestContactUpdate/components/RequestContactUpdateFormTitle";
import { entitledAccountsByFundLoadingSelector } from "@store/Entities/selectors";
import { fetchEntitledAccountsByEntity } from "@store/Entities/thunks";
import useMinimumLoadingDuration from "@shared/hooks/useMinimumLoadingDuration";

// Constants for loading duration
const CASCADING_FILTER_LOADING_DURATION_MS = 800;

const RequestContactUpdate = () => {
  const { funds, accounts, message, contactName, contactEmail } = useAppSelector(
    requestContactUpdatePreparedDataSelector
  );
  const { validationWarningShown, commonFields } = useAppSelector(requestContactUpdateSelector);
  const accountsByFundLoading = useAppSelector(entitledAccountsByFundLoadingSelector);
  const dispatch = useAppDispatch();

  // State for triggered loading when filters change (for UX feedback)
  const [fundsTriggeredLoading, setFundsTriggeredLoading] = useState(false);
  const [accountsTriggeredLoading, setAccountsTriggeredLoading] = useState(false);

  // Track previous values to detect changes
  const prevSelectedFundsRef = useRef<string[]>([]);
  const prevSelectedAccountsRef = useRef<string[]>([]);

  // Use minimum loading duration for better UX
  const showAccountsLoading = useMinimumLoadingDuration(
    accountsByFundLoading || accountsTriggeredLoading,
    CASCADING_FILTER_LOADING_DURATION_MS
  );
  const showFundsLoading = useMinimumLoadingDuration(fundsTriggeredLoading, CASCADING_FILTER_LOADING_DURATION_MS);

  // Fetch entitledAccountsByEntity when funds are selected (like Documents page)
  // This populates entitledAccountIds on the funds for cascading filter
  useEffect(() => {
    if (commonFields.selectedFunds.length > 0) {
      commonFields.selectedFunds.forEach((fundId) => {
        dispatch(fetchEntitledAccountsByEntity(fundId));
      });
    }
  }, [dispatch, commonFields.selectedFunds]);

  // Trigger loading when funds selection changes
  useEffect(() => {
    const prevFunds = prevSelectedFundsRef.current;
    const currentFunds = commonFields.selectedFunds;

    if (JSON.stringify(prevFunds) !== JSON.stringify(currentFunds) && currentFunds.length > 0) {
      setAccountsTriggeredLoading(true);
      setTimeout(() => setAccountsTriggeredLoading(false), 100);
    }

    prevSelectedFundsRef.current = currentFunds;
  }, [commonFields.selectedFunds]);

  // Trigger loading when accounts selection changes
  useEffect(() => {
    const prevAccounts = prevSelectedAccountsRef.current;
    const currentAccounts = commonFields.selectedAccounts;

    if (JSON.stringify(prevAccounts) !== JSON.stringify(currentAccounts) && currentAccounts.length > 0) {
      setFundsTriggeredLoading(true);
      setTimeout(() => setFundsTriggeredLoading(false), 100);
    }

    prevSelectedAccountsRef.current = currentAccounts;
  }, [commonFields.selectedAccounts]);

  const handleMessageChange = useCallback(
    (message: string) => dispatch(updateCommonRequestData({ message })),
    [dispatch]
  );

  const handleContactNameChange = useCallback(
    (contactName: string) => {
      // Limit to max length
      const trimmed = contactName.slice(0, MAX_CONTACT_NAME_LENGTH);

      dispatch(updateCommonRequestData({ contactName: trimmed }));
    },
    [dispatch]
  );

  const handleContactEmailChange = useCallback(
    (contactEmail: string) => {
      // Limit to max length
      const trimmed = contactEmail.slice(0, MAX_CONTACT_EMAIL_LENGTH);

      dispatch(updateCommonRequestData({ contactEmail: trimmed }));
    },
    [dispatch]
  );

  const contactNameExceedsMax = commonFields.contactName.length > MAX_CONTACT_NAME_LENGTH;
  const contactEmailExceedsMax = commonFields.contactEmail.length > MAX_CONTACT_EMAIL_LENGTH;

  return (
    <>
      <div data-testid="request-contact-update" />
      <RequestContactUpdateFormTitle text="Related Contact:*" />
      <RequestContactUpdateInput
        contactName={contactName}
        contactEmail={contactEmail}
        onContactNameChange={handleContactNameChange}
        onContactEmailChange={handleContactEmailChange}
        contactNameError={
          validationWarningShown && (!commonFields.contactName.trim() || !isValidName(commonFields.contactName))
        }
        contactEmailError={
          validationWarningShown && (!commonFields.contactEmail.trim() || !isValidEmail(commonFields.contactEmail))
        }
        contactNameExceedsMax={contactNameExceedsMax}
        contactEmailExceedsMax={contactEmailExceedsMax}
        maxContactNameLength={MAX_CONTACT_NAME_LENGTH}
        maxContactEmailLength={MAX_CONTACT_EMAIL_LENGTH}
        showErrors={validationWarningShown}
      />
      <RequestContactUpdateFormTitle text="Related Funds/Accounts:" />
      <RequestContactUpdateEntitiesDropdowns
        accounts={accounts}
        funds={funds}
        accountsLoading={showAccountsLoading}
        fundsLoading={showFundsLoading}
      />
      <RequestContactUpdateFormTitle text="What modifications need to be made to the contact?*" />
      <RequestContactUpdateTextarea
        placeholder="You can request to remove a contact, update their role, document access, or adjust their notification settings."
        value={message}
        onChange={handleMessageChange}
        error={validationWarningShown && !message}
        showErrors={validationWarningShown}
      />
    </>
  );
};

export default RequestContactUpdate;
